<div
  cdkDropList
  (cdkDropListDropped)="drop($event)"
  class="divide-y divide-slate-100 min-h-[50px] transition-opacity"
  [class.pointer-events-none]="saving"
>
  @for (item of items(); track item.id; let i = $index) {
  <div
    cdkDrag
    class="p-5 group hover:bg-slate-50/50 transition-colors relative bg-white"
    [class.cdk-drag-dragging]="'z-50 opacity-50'"
    [class.opacity-60]="saving && activeItemId() !== item.id"
  >
    <div
      *cdkDragPlaceholder
      class="h-16 bg-blue-50/50 border-2 border-blue-400 border-dashed rounded-lg"
    ></div>

    <div class="flex items-center gap-4">
      <div
        cdkDragHandle
        class="text-slate-300 cursor-grab active:cursor-grabbing hover:text-slate-500 transition-colors"
      >
        <lucide-angular [img]="GripIcon" class="w-4 h-4"></lucide-angular>
      </div>

      <span class="text-slate-400 text-sm font-medium w-4"
        >{{ item.option_key }}
      </span>
      <div
        class="flex-1 flex items-center gap-2"
        [formGroup]="form"
        tabindex=""
        role="button"
      >
        @if (item.is_mutually_exclusive) {
        <span
          class="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold uppercase"
          >Exclusive</span
        >
        } @else if (item.is_open_ended) {
        <span
          class="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold uppercase"
          >Open Ended</span
        >
        } @if (activeItemId() === item.id) {
        <input
          type="text"
          formControlName="option_display"
          (keydown.enter)="handleEnterKey($event)"
          [placeholder]="!item.is_mutually_exclusive && !item.is_open_ended ? 'Enter item text...' : ''"
          class="flex-1 bg-transparent text-slate-800 border-none focus:ring-0 p-0 text-base outline-none py-3 min-h-[48px]"
          (click)="$event.stopPropagation()"
          (mousedown)="$event.stopPropagation()"
          #activeInput
          [attr.data-item-id]="item.id"
        />
        } @else {
        <input
          type="text"
          [value]="item.option_display"
          (focus)="setActiveOption(item)"
          readonly
          [placeholder]="!item.is_mutually_exclusive && !item.is_open_ended ? 'Enter item text...' : ''"
          class="flex-1 bg-transparent text-slate-800 border-none focus:ring-0 p-0 text-base outline-none py-3 min-h-[48px]"
          (click)="$event.stopPropagation()"
          (mousedown)="$event.stopPropagation()"
          [attr.data-item-id]="item.id"
        />
        }
      </div>

      <!-- Image Button: Normal options only -->
      @if (!item.is_mutually_exclusive && !item.is_open_ended) {
      <button
        type="button"
        title="Add image"
        (click)="handleAddImage(item)"
        (mousedown)="$event.stopPropagation()"
        [disabled]="saving"
        class="p-2 text-slate-300 hover:text-blue-600 rounded-lg transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 disabled:opacity-40"
      >
        <lucide-angular [img]="ImagePlusIcon" class="w-5 h-5"></lucide-angular>
      </button>
      }
      <!-- Input extra buttons -->
      @if ( extraButton() === 'gear' && !item.is_mutually_exclusive &&
      !item.is_open_ended ) {
      <button
        type="button"
        title="Configure option"
        (click)="handleConfigurationSetting(item.id)"
        (mousedown)="$event.stopPropagation()"
        [disabled]="saving"
        class="p-2 text-slate-300 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100 disabled:opacity-40"
      >
        <lucide-angular [img]="SettingsIcon" class="w-5 h-5"></lucide-angular>
      </button>
      }
      <button
        (click)="removeItem(item.id!)"
        class="p-2 text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
        (mousedown)="$event.stopPropagation()"
      >
        <lucide-angular [img]="TrashIcon" class="w-5 h-5"></lucide-angular>
      </button>
    </div>
  </div>
  } @empty {
  <div class="p-10 text-center text-slate-400 text-sm italic">
    No items added yet. Click "Add Items" to begin.
  </div>
  }
</div>

<div class="p-6 border-t border-slate-100 flex items-center gap-6 bg-white">
  <button
    (click)="addItem(false, false)"
    class="text-blue-600 font-semibold hover:text-blue-700 transition-colors text-sm flex items-center gap-1 bg-transparent border-none cursor-pointer p-0"
  >
    <lucide-angular [img]="PlusIcon" class="w-4 h-4"></lucide-angular>
    Add Items
  </button>

  <button
    [matMenuTriggerFor]="specialMenu"
    class="text-blue-600 font-semibold hover:text-blue-700 transition-colors text-sm flex items-center gap-1 bg-transparent border-none cursor-pointer p-0"
  >
    Add Special Items
    <lucide-angular [img]="ChevronDownIcon" class="w-3 h-3"></lucide-angular>
  </button>

  <mat-menu
    #specialMenu="matMenu"
    class="!rounded-xl !shadow-xl !border !border-slate-200 !mt-2"
  >
    <!-- Pass true, false for exclusive -->
    <button
      mat-menu-item
      (click)="addItem(true, false)"
      class="!h-auto !py-3 !px-4 hover:!bg-slate-50"
    >
      <div class="flex flex-col leading-tight">
        <span class="font-semibold text-slate-700 text-sm"
          >Mutually Exclusive</span
        >
        <span class="text-xs text-slate-500"
          >Deselects other options when chosen</span
        >
      </div>
    </button>
    <!-- Pass false, true for open ended -->
    <button
      mat-menu-item
      (click)="addItem(false, true)"
      class="!h-auto !py-3 !px-4 hover:!bg-slate-50"
    >
      <div class="flex flex-col leading-tight">
        <span class="font-semibold text-slate-700 text-sm"
          >Open Ended Text</span
        >
        <span class="text-xs text-slate-500"
          >Includes a text field for "Other"</span
        >
      </div>
    </button>
  </mat-menu>

  <button
    (click)="addBulkOptions()"
    class="text-blue-600 font-semibold hover:text-blue-700 transition-colors text-sm flex items-center gap-1 bg-transparent border-none cursor-pointer p-0"
  >
    <lucide-angular [img]="PlusIcon" class="w-4 h-4"></lucide-angular>
    Add Bulk Items
  </button>
</div>
